import fetch from '~/common/fetch'

/**
 * AnalyticsApiResponse: Structure of analytics data returned from backend API
 *
 * Analytics Metadata:
 * - mixpanel_analytics_metadata:
 *   - enabled: Mixpanel settings-based control (boolean)
 *   - token: Mixpanel project token (string)
 *   - mixpanel_user_id: Hashed and salted user identifier for Mixpanel (string, non-PII)
 *
 * Shared Metadata:
 * - tenant: Current schema/tenant name (string)
 * - tenant_locale: Tenant locale setting (string)
 * - tenant_timezone: Tenant timezone setting (string)
 * - domain: Environment name - 'production' | 'qa' | 'beta' | 'local'
 * - environment_type: Environment category - 'production' | 'test'
 * - tags_owner: Tenant tag owner - 'customer' | 'partner' | 'internal'
 * - tags_purpose: Tenant tag purpose - 'production' | 'test' | 'training' | 'demo' | 'infra' | 'quality_assurance' | 'public'
 * - rbac_role: User RBAC role(s) for tracking (string)
 * - role: User business role - 'anonymous user' | 'Candidate' | 'Employer' | 'Vendor' | 'WT Worker'
 * - user_locale: User-specific locale setting (string)
 * - user_timezone: User-specific timezone setting (string)
 */
export type AnalyticsApiResponse = {
  mixpanel_analytics_metadata: {
    enabled: boolean
    token: string
    mixpanel_user_id: string // Hashed and salted, non-PII unique id for Mixpanel analytics
  }

  tenant: string
  tenant_locale: string
  tenant_timezone: string
  domain: 'production' | 'qa' | 'beta' | 'local'
  environment_type: 'production' | 'test'
  tags_owner: 'customer' | 'partner' | 'internal'
  tags_purpose: 'production' | 'test' | 'training' | 'demo' | 'infra' | 'quality_assurance' | 'public'
  rbac_role: string
  role: 'anonymous user' | 'Candidate' | 'Employer' | 'Vendor' | 'WT Worker'
  user_locale: string
  user_timezone: string
}

const ONE_HOUR = 1000 * 60
const responseCache = { ttl: ONE_HOUR }
export const analyticsApi = {
  config: (): Promise<AnalyticsApiResponse> => fetch.get(`/api/v2/analytics/config/`, { responseCache })
}
